import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import Cookies from 'js-cookie'
import { type JSX, useEffect, useState } from 'react'
import { z } from 'zod'
import { useAuthenticator } from '~/features/auth'
import { OrganizationAvatar } from '~/features/avatar'
import { LinkButton } from '~/features/navigation'
import { getAuthToken, unpackAuthToken } from '~/helpers/sessionHelpers'
import { PageLevelError, ServiceUnavailableError } from '~/modules/error-boundary'
import { isServiceUnavailableError, useTRPC } from '~/services/trpc'
import { PublicTemplate } from '~/templates/public'

import {
  Button,
  Heading1,
  Icon,
  Input,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@hap/components'

export const Route = createFileRoute('/_authenticated/')({
  component: Authenticated,
  validateSearch: z.object({
    force: z.boolean().optional(),
  }),
})

const lastOrgAlias = Cookies.get('last-org-alias')

function Authenticated(): JSX.Element {
  const { force } = Route.useSearch()
  const trpc = useTRPC()
  const [authenticate, isPending] = useAuthenticator()
  const [filterValue, setFilterValue] = useState('')
  const [fetchOrgs, setFetchOrgs] = useState(false)
  const currentUser = unpackAuthToken(getAuthToken())

  const {
    data: organizations,
    isLoading,
    error,
    isFetching,
    refetch,
  } = useQuery(
    trpc.organizations.getAll.queryOptions(undefined, {
      enabled: fetchOrgs,
    }),
  )

  useEffect(() => {
    if (organizations?.length === 1) {
      authenticate({
        legacyId: organizations[0].legacyId,
        organizationId: organizations[0].id,
        default: true,
      })
    } else if (!force) {
      if (lastOrgAlias && organizations) {
        const lastOrg = organizations.find((org) => org.alias === lastOrgAlias)
        if (lastOrg) {
          authenticate({
            legacyId: lastOrg.legacyId,
            organizationId: lastOrg.id,
            default: false,
          })
          return
        }
      }
      setFetchOrgs(true)
    } else if (force) {
      setFetchOrgs(true)
    }
  }, [organizations])

  if (error && isServiceUnavailableError(error)) {
    return (
      <ServiceUnavailableError isRetrying={isFetching} onRetry={() => void refetch()} />
    )
  }

  if (error) {
    return (
      <PageLevelError
        title="Unable to load your organizations"
        description="Please try again. If the problem continues, contact support."
        actions={
          <Button loading={isFetching} onClick={() => void refetch()}>
            Try again
          </Button>
        }
      />
    )
  }

  if (isLoading || organizations?.length === 1 || isPending || !fetchOrgs) {
    return (
      <Spinner className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
    )
  }
  const isRedirecting =
    organizations?.length &&
    organizations.length > 1 &&
    !force &&
    lastOrgAlias &&
    organizations.some((org) => org.alias === lastOrgAlias)
  if (isRedirecting) {
    return (
      <Spinner className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
    )
  }

  return (
    <PublicTemplate
      width={'100%'}
      style={{
        justifySelf: 'stretch',
        alignSelf: 'stretch',
        placeContent: 'start center',
        flexGrow: 1,
      }}
    >
      <div className="grow">
        <div className="absolute top-3 left-3 flex items-center gap-3 leading-tight">
          <div>
            <p className="text-foreground/60">Signed in as:</p>
            <p>{currentUser?.email || 'User without session email'}</p>
          </div>
        </div>

        <LinkButton to="/logout" variant="secondary" className="absolute top-3 right-3">
          Log Out
        </LinkButton>
        <header className="mb-10 grid gap-4 text-center">
          <Heading1>Welcome back! 👋</Heading1>
          <p className="text-accent-foreground mb-10 text-lg">
            {organizations?.length
              ? 'Choose an organization below to get back to your dashboard.'
              : 'Your user currently has no access to any organizations.'}
          </p>
          {organizations?.length ? (
            <Input
              placeholder="Search..."
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const filteredOrg = organizations.find(
                    (org) =>
                      filterValue === org.legacyId ||
                      org.name.toLowerCase().includes(filterValue.toLowerCase()),
                  )
                  if (filteredOrg) {
                    authenticate({
                      legacyId: filteredOrg.legacyId,
                      organizationId: filteredOrg.id,
                      default: false,
                    })
                  }
                }
              }}
              autoFocus
              className="mx-auto max-w-[400px]"
            />
          ) : (
            <LinkButton
              to="/logout"
              variant="secondary"
              className="absolute top-3 right-3"
            >
              Log Out
            </LinkButton>
          )}
        </header>

        <section className="3xl:grid-cols-6 grid gap-4 p-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {organizations
            ?.filter(
              (org) =>
                filterValue === org.legacyId ||
                org.name.toLowerCase().includes(filterValue.toLowerCase()),
            )
            .map((org) => (
              <a
                key={org.id}
                className="bg-accent group flex cursor-pointer items-center gap-4 p-4 transition-transform hover:scale-103 hover:shadow-md"
                onClick={() => {
                  authenticate({
                    legacyId: org.legacyId,
                    organizationId: org.id,
                    default: false,
                  })
                }}
              >
                <OrganizationAvatar name={org.name} />
                <div className="grow">{org.name}</div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button rounded className="opacity-0 group-hover:opacity-100">
                      <Icon name="arrow-right" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Access Organization</TooltipContent>
                </Tooltip>
              </a>
              // <CommandItem
              //   onSelect={() =>
              //     authenticate({
              //       legacyId: org.legacyId,
              //       organizationId: org.id,
              //       default: false,
              //     })
              //   }
              //   key={org.id}
              //   keywords={[org.name, org.legacyId]}
              //   className="border-b-background group flex cursor-pointer items-center gap-3 rounded-none border-b px-6 py-2 last:border-b-0 hover:rounded-md"
              // >
              //   <OrganizationAvatar name={org.name} size="md" />
              //   <div className="grid grow">
              //     <h2 className="font-medium">{org.name}</h2>
              //   </div>
              //   <div className="shrink-0 opacity-0 group-data-[selected=true]:opacity-100">
              //     <Button rounded size="sm">
              //       Launch
              //       <Icon name="arrow-right" />
              //     </Button>
              //   </div>
              // </CommandItem>
            ))}
        </section>
      </div>
    </PublicTemplate>
  )
}
